import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  PlmEvent,
  useCreatePlmEventMutation,
  useUpdatePlmEventMutation,
  usePlmEventProgramOptionsQuery,
  usePlmContractOptionsQuery,
  EnumPlmEventStatus,
  EnumPlmEventNotificationMethod,
} from '../../../../types/generated-types';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import BorderedSection from '../../shared/borderedSection';
import { Notifier } from '../../../system/services/notificationManager';
import { useAuthorizer } from '../../../auth/AuthorizationContext';
import { useInjectableComponents } from '../../../system/services/injectableComponentsManager';
import { useIsSmallScreen } from '../../../system/SystemMediaQueriesProvider';
import SaveIcon from '@mui/icons-material/Save';
import '../ui/plm.css';

const validationSchema = Yup.object({
  programId: Yup.string().required('Program is required'),
  contractId: Yup.string().required('Contract is required'),
  startTime: Yup.number().required('Start time is required'),
  duration: Yup.number()
    .required('Duration is required')
    .min(1, 'Duration must be at least 1 minute'),
  reductionTarget: Yup.number().nullable(),
  status: Yup.string().required('Status is required'),
  notificationMethod: Yup.string().nullable(),
  notificationReceivedAt: Yup.number().nullable(),
  notificationPayload: Yup.string().nullable(),
});

const calculatePlmEventFormValues = (plmEvent?: Partial<PlmEvent>) => ({
  programId: plmEvent?.programId || '',
  contractId: plmEvent?.contractId || '',
  startTime: plmEvent?.startTime || Date.now(),
  duration: plmEvent?.duration || 60,
  reductionTarget: plmEvent?.reductionTarget || null,
  status: plmEvent?.status || EnumPlmEventStatus.Planned,
  notificationMethod: plmEvent?.notificationMethod || null,
  notificationReceivedAt: plmEvent?.notificationReceivedAt || null,
  notificationPayload: plmEvent?.notificationPayload || null,
});

interface PlmEventDetailFormProps {
  plmEvent?: PlmEvent;
  onCancel?: () => void;
  onSuccess?: () => void;
}

export const PlmEventDetailForm: React.FC<PlmEventDetailFormProps> = ({
  plmEvent,
  onCancel,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { can } = useAuthorizer();
  const { setTitle, setTitlePath } = useInjectableComponents();
  const isEditing = !!id;

  const [createPlmEvent] = useCreatePlmEventMutation();
  const [updatePlmEvent] = useUpdatePlmEventMutation();

  const { data: programOptions } = usePlmEventProgramOptionsQuery();
  const { data: contractOptions } = usePlmContractOptionsQuery();

  const isSmall = useIsSmallScreen();
  // Set title and breadcrumb
  useEffect(() => {
    const title = isEditing
      ? isSmall
        ? 'Edit PLM Event'
        : 'Edit Peak Load Management Event'
      : isSmall
        ? 'Add PLM Event'
        : 'Create Peak Load Management Event';
    const titlePath = '/plm-events';

    setTitle(title);
    setTitlePath(titlePath);
  }, [isEditing, setTitle, setTitlePath, isSmall]);

  const formik = useFormik({
    initialValues: calculatePlmEventFormValues(plmEvent),
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (isEditing) {
          if (!can('update', 'PlmEvent')) {
            Notifier.error('You do not have permission to update PLM events');
            return;
          }

          await updatePlmEvent({
            variables: {
              _id: id!,
              plmEvent: {
                programId: values.programId,
                contractId: values.contractId,
                startTime: values.startTime,
                duration: values.duration,
                reductionTarget: values.reductionTarget,
                status: values.status,
                notificationMethod: values.notificationMethod,
                notificationReceivedAt: values.notificationReceivedAt,
                notificationPayload: values.notificationPayload,
              },
            },
          });
          Notifier.success('PLM event updated successfully');
        } else {
          if (!can('create', 'PlmEvent')) {
            Notifier.error('You do not have permission to create PLM events');
            return;
          }

          await createPlmEvent({
            variables: {
              record: {
                programId: values.programId,
                contractId: values.contractId,
                startTime: values.startTime,
                duration: values.duration,
                reductionTarget: values.reductionTarget,
                status: values.status,
                notificationMethod: values.notificationMethod,
                notificationReceivedAt: values.notificationReceivedAt,
                notificationPayload: values.notificationPayload,
              },
            },
          });
          Notifier.success('PLM event created successfully');
        }

        if (onSuccess) {
          onSuccess();
        } else {
          navigate('/plm-events');
        }
      } catch (error) {
        Notifier.error(
          `Failed to ${isEditing ? 'update' : 'create'} PLM event`,
        );
      }
    },
  });

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/plm-events');
    }
  };

  return (
    <Box
      component="form"
      sx={{ padding: '6px' }}
      onSubmit={formik.handleSubmit}
    >
      <Typography variant="h4" gutterBottom>
        {isEditing ? 'Edit PLM Event' : 'Create PLM Event'}
      </Typography>

      <Grid container spacing={1} className="plm-grid-container">
        <Grid item xs={12} className="plm-item-container">
          <BorderedSection title="Basic Information">
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  error={formik.touched.programId && !!formik.errors.programId}
                >
                  <InputLabel>Program</InputLabel>
                  <Select
                    name="programId"
                    value={formik.values.programId}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Program"
                  >
                    {programOptions?.plmPrograms?.map((program) => (
                      <MenuItem key={program._id} value={program._id}>
                        {program.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.programId && formik.errors.programId && (
                    <Typography color="error" variant="caption">
                      {formik.errors.programId}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  error={
                    formik.touched.contractId && !!formik.errors.contractId
                  }
                >
                  <InputLabel>Contract</InputLabel>
                  <Select
                    name="contractId"
                    value={formik.values.contractId}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Contract"
                  >
                    {contractOptions?.plmContracts?.map((contract) => (
                      <MenuItem key={contract._id} value={contract._id}>
                        {contract.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.contractId && formik.errors.contractId && (
                    <Typography color="error" variant="caption">
                      {formik.errors.contractId}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="startTime"
                  label="Start Time"
                  type="datetime-local"
                  value={new Date(formik.values.startTime)
                    .toISOString()
                    .slice(0, 16)}
                  onChange={(e) => {
                    const timestamp = new Date(e.target.value).getTime();
                    formik.setFieldValue('startTime', timestamp);
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.startTime && !!formik.errors.startTime}
                  helperText={
                    formik.touched.startTime && formik.errors.startTime
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="duration"
                  label="Duration (minutes)"
                  type="number"
                  value={formik.values.duration}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.duration && !!formik.errors.duration}
                  helperText={formik.touched.duration && formik.errors.duration}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="reductionTarget"
                  label="Reduction Target (kW)"
                  type="number"
                  value={formik.values.reductionTarget || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.reductionTarget &&
                    !!formik.errors.reductionTarget
                  }
                  helperText={
                    formik.touched.reductionTarget &&
                    formik.errors.reductionTarget
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  error={formik.touched.status && !!formik.errors.status}
                >
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formik.values.status}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Status"
                  >
                    {Object.values(EnumPlmEventStatus).map((status) => (
                      <MenuItem key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.status && formik.errors.status && (
                    <Typography color="error" variant="caption">
                      {formik.errors.status}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </BorderedSection>
        </Grid>

        <Grid item xs={12}>
          <BorderedSection title="Event Notification">
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  error={
                    formik.touched.notificationMethod &&
                    !!formik.errors.notificationMethod
                  }
                >
                  <InputLabel>Notification Method</InputLabel>
                  <Select
                    name="notificationMethod"
                    value={formik.values.notificationMethod || ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Notification Method"
                  >
                    <MenuItem value="">Not specified</MenuItem>
                    {Object.values(EnumPlmEventNotificationMethod).map(
                      (method) => (
                        <MenuItem key={method} value={method}>
                          {method.toUpperCase()}
                        </MenuItem>
                      ),
                    )}
                  </Select>
                  {formik.touched.notificationMethod &&
                    formik.errors.notificationMethod && (
                      <Typography color="error" variant="caption">
                        {formik.errors.notificationMethod}
                      </Typography>
                    )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="notificationReceivedAt"
                  label="Notification Received At"
                  type="datetime-local"
                  value={
                    formik.values.notificationReceivedAt
                      ? new Date(formik.values.notificationReceivedAt)
                          .toISOString()
                          .slice(0, 16)
                      : ''
                  }
                  onChange={(e) => {
                    const timestamp = e.target.value
                      ? new Date(e.target.value).getTime()
                      : null;
                    formik.setFieldValue('notificationReceivedAt', timestamp);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.notificationReceivedAt &&
                    !!formik.errors.notificationReceivedAt
                  }
                  helperText={
                    formik.touched.notificationReceivedAt &&
                    formik.errors.notificationReceivedAt
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="notificationPayload"
                  label="Notification Body"
                  multiline
                  rows={4}
                  value={formik.values.notificationPayload || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.notificationPayload &&
                    !!formik.errors.notificationPayload
                  }
                  helperText={
                    formik.touched.notificationPayload &&
                    formik.errors.notificationPayload
                  }
                  placeholder="Enter the raw notification message body..."
                />
              </Grid>
            </Grid>
          </BorderedSection>
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
        <Button variant="outlined" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          startIcon={<SaveIcon />}
          type="submit"
          variant="contained"
          color="secondary"
          disabled={formik.isSubmitting}
        >
          {formik.isSubmitting
            ? 'Saving...'
            : isEditing
              ? 'Update PLM Event'
              : 'Create PLM Event'}
        </Button>
      </Box>
    </Box>
  );
};
